import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import {
  cnpjSchema,
  formatCnpj,
  sanitizeCnpj,
  TAX_REGIME_LABELS,
  type CnpjInput,
  type TaxRegime,
} from '@/lib/zodSchemas/cnpj';
import { api } from '@/lib/api';
import type { WizardState } from '../wizardState';

interface Props {
  state: WizardState;
  onComplete: (patch: Partial<WizardState>) => void;
  onBack: () => void;
}

const REGIMES = Object.keys(TAX_REGIME_LABELS) as TaxRegime[];

export function CnpjStep({ state, onComplete, onBack }: Props) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CnpjInput>({
    resolver: zodResolver(cnpjSchema),
    defaultValues: {
      cnpj: state.cnpjSummary?.cnpj ?? '',
      razaoSocial: state.cnpjSummary?.razaoSocial ?? '',
      taxRegime: 'SIMPLES_III',
      cep: '',
      street: '',
      number: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      municipalityCode: '',
      inscricaoMunicipal: '',
      cnae: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const payload = {
      cnpj: values.cnpj,
      legalName: values.razaoSocial,
      taxRegime: values.taxRegime,
      municipalityCode: values.municipalityCode,
      stateCode: values.state,
      inscricaoMunicipal: values.inscricaoMunicipal,
      ...(values.cnae ? { cnae: values.cnae } : {}),
      address: {
        cep: values.cep,
        logradouro: values.street,
        numero: values.number,
        bairro: values.district,
        cidade: values.city,
        uf: values.state,
        ...(values.complement ? { complemento: values.complement } : {}),
      },
    };
    const created = await api.post<{ id: string }>('/cnpjs', payload);
    onComplete({
      cnpjId: created.id,
      cnpjSummary: { cnpj: values.cnpj, razaoSocial: values.razaoSocial },
    });
  });

  const handleCepBlur = async (cep: string) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!res.ok) return;
      const data = (await res.json()) as {
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
        ibge?: string;
      };
      if (data.logradouro) setValue('street', data.logradouro);
      if (data.bairro) setValue('district', data.bairro);
      if (data.localidade) setValue('city', data.localidade);
      if (data.uf) setValue('state', data.uf);
      if (data.ibge) setValue('municipalityCode', data.ibge);
    } catch {
      // viacep offline — user fills manually
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <div>
        <h2 className="font-display text-2xl font-semibold">Seu CNPJ</h2>
        <p className="mt-1 text-sm text-(--color-fg-muted)">
          Dados fiscais usados na emissão da NFS-e. Você pode adicionar outros depois.
        </p>
      </div>

      <FormField id="cnpj" label="CNPJ" error={errors.cnpj?.message} required>
        <Controller
          control={control}
          name="cnpj"
          render={({ field }) => (
            <Input
              id="cnpj"
              inputMode="numeric"
              placeholder="12.345.678/0001-90"
              invalid={!!errors.cnpj}
              value={formatCnpj(field.value ?? '')}
              onChange={(e) => field.onChange(sanitizeCnpj(e.target.value))}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          )}
        />
      </FormField>

      <FormField id="razaoSocial" label="Razão social" error={errors.razaoSocial?.message} required>
        <Input id="razaoSocial" invalid={!!errors.razaoSocial} {...register('razaoSocial')} />
      </FormField>

      <FormField id="taxRegime" label="Regime tributário" error={errors.taxRegime?.message} required>
        <select
          id="taxRegime"
          {...register('taxRegime')}
          className="flex h-11 w-full rounded-md border border-(--color-border) bg-(--color-surface) px-3 text-sm text-(--color-fg) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
        >
          {REGIMES.map((r) => (
            <option key={r} value={r}>
              {TAX_REGIME_LABELS[r]}
            </option>
          ))}
        </select>
      </FormField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField id="cep" label="CEP" error={errors.cep?.message} required>
          <Input
            id="cep"
            inputMode="numeric"
            placeholder="00000-000"
            invalid={!!errors.cep}
            {...register('cep', {
              onBlur: (e) => void handleCepBlur(e.target.value),
            })}
          />
        </FormField>
        <FormField id="number" label="Número" error={errors.number?.message} required>
          <Input id="number" invalid={!!errors.number} {...register('number')} />
        </FormField>
      </div>

      <FormField id="street" label="Logradouro" error={errors.street?.message} required>
        <Input id="street" invalid={!!errors.street} {...register('street')} />
      </FormField>
      <FormField id="complement" label="Complemento">
        <Input id="complement" {...register('complement')} />
      </FormField>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormField id="district" label="Bairro" error={errors.district?.message} required>
          <Input id="district" invalid={!!errors.district} {...register('district')} />
        </FormField>
        <FormField id="city" label="Cidade" error={errors.city?.message} required>
          <Input id="city" invalid={!!errors.city} {...register('city')} />
        </FormField>
        <FormField id="state" label="UF" error={errors.state?.message} required>
          <Input
            id="state"
            maxLength={2}
            invalid={!!errors.state}
            className="uppercase"
            {...register('state')}
          />
        </FormField>
      </div>
      <FormField
        id="municipalityCode"
        label="Código IBGE do município"
        error={errors.municipalityCode?.message}
        required
      >
        <Input
          id="municipalityCode"
          inputMode="numeric"
          placeholder="Preenchido automaticamente via CEP"
          invalid={!!errors.municipalityCode}
          {...register('municipalityCode')}
        />
      </FormField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          id="inscricaoMunicipal"
          label="Inscrição Municipal (CCM)"
          required
          hint="Necessária para autorizar a emissão de NFS-e (Padrão Nacional)"
          error={errors.inscricaoMunicipal?.message}
        >
          <Input
            id="inscricaoMunicipal"
            inputMode="numeric"
            placeholder="Inscrição na prefeitura"
            invalid={!!errors.inscricaoMunicipal}
            {...register('inscricaoMunicipal')}
          />
        </FormField>
        <FormField id="cnae" label="CNAE principal" error={errors.cnae?.message}>
          <Input
            id="cnae"
            placeholder="Ex.: 8630-5/03"
            invalid={!!errors.cnae}
            {...register('cnae')}
          />
        </FormField>
      </div>

      <div className="mt-2 flex justify-between gap-3">
        <Button type="button" variant="ghost" onClick={onBack}>
          Voltar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando…' : 'Continuar'}
        </Button>
      </div>
    </form>
  );
}
